import {
  Building2,
  Check,
  Copy,
  Link2,
  Loader2,
  Search,
  Shield,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { authService, sharingService } from "@/lib/services";
import type {
  DataResponse,
  SessionSharingResponse,
  ShareLinkResponse,
  SharePermission,
  ShareResponse,
  Team,
  TeamShareResponse,
  UserSearchResult,
} from "@/types";

// ---------------------------------------------------------------------------
// Service adapter — abstracts which backend endpoints to call
// ---------------------------------------------------------------------------

export interface ShareServiceAdapter {
  getShares: (entityId: string) => Promise<DataResponse<SessionSharingResponse>>;
  shareWithUser: (
    entityId: string,
    userUuid: string,
    permission: SharePermission,
  ) => Promise<DataResponse<ShareResponse>>;
  removeShare: (entityId: string, userUuid: string) => Promise<unknown>;
  createShareLink: (entityId: string, permission: SharePermission) => Promise<DataResponse<ShareLinkResponse>>;
  removeShareLink: (entityId: string, linkUuid: string) => Promise<unknown>;
}

export interface EntityShareDialogProps {
  entityId: string;
  entityTitle: string;
  isOpen: boolean;
  onClose: () => void;
  /** Human-readable noun: "Chat", "Brainstorm", "Canvas", "Task", "Deep Research" */
  entityLabel: string;
  /** Backend entity type key for team sharing API: "chat_session", "group_chat", etc. */
  entityType: string;
  /** URL path segment used in share links, e.g. "/chat/shared/" — required if service is provided */
  shareLinkPath?: string;
  /** Service adapter providing CRUD operations for user shares & links — if omitted, only team sharing is available */
  service?: ShareServiceAdapter;
}

export function EntityShareDialog({
  // NOSONAR -- share dialog with people/team/link tabs and CRUD operations
  entityId,
  entityTitle,
  isOpen,
  onClose,
  entityLabel,
  entityType,
  shareLinkPath,
  service,
}: Readonly<EntityShareDialogProps>) {
  const { teams } = useApp();
  const hasFullSharing = !!service;
  const [activeTab, setActiveTab] = useState<"people" | "team" | "link">(hasFullSharing ? "people" : "team");
  const [shares, setShares] = useState<ShareResponse[]>([]);
  const sharesRef = useRef(shares);
  sharesRef.current = shares;
  const [links, setLinks] = useState<ShareLinkResponse[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);

  // People tab state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [sharePermission, setSharePermission] = useState<SharePermission>("read");
  const [isSharing, setIsSharing] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Team tab state
  const [teamShares, setTeamShares] = useState<TeamShareResponse[]>([]);
  const [isLoadingTeamShares, setIsLoadingTeamShares] = useState(false);
  const [sharingTeamId, setSharingTeamId] = useState<string | null>(null);
  const [removingTeamShareId, setRemovingTeamShareId] = useState<string | null>(null);
  const [teamSharePermission, setTeamSharePermission] = useState<SharePermission>("read");

  // Link tab state
  const [linkPermission, setLinkPermission] = useState<SharePermission>("read");
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);

  // Error/success state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadShares = useCallback(async () => {
    if (!service) return;
    try {
      setIsLoadingShares(true);
      const response = await service.getShares(entityId);
      setShares(response.data.shares);
      setLinks(response.data.links);
    } catch (err) {
      console.error("Failed to load shares:", err);
      setError("Failed to load sharing info");
    } finally {
      setIsLoadingShares(false);
    }
  }, [entityId, service]);

  const loadTeamShares = useCallback(async () => {
    try {
      setIsLoadingTeamShares(true);
      const response = await sharingService.getTeamShares(entityType, entityId);
      setTeamShares(response.data.teamShares);
    } catch (err) {
      console.error("Failed to load team shares:", err);
    } finally {
      setIsLoadingTeamShares(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (isOpen) {
      loadShares();
      loadTeamShares();
      setError(null);
      setSuccessMessage(null);
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [isOpen, loadShares, loadTeamShares]);

  // Debounced user search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await authService.searchUsers(searchQuery);
        // Filter out users already shared with
        const sharedUserUuids = new Set(sharesRef.current.map((s) => s.sharedWith.uuid));
        setSearchResults(response.data.filter((u) => !sharedUserUuids.has(u.uuid)));
      } catch (err) {
        console.error("Failed to search users:", err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleShareWithUser = async (user: UserSearchResult) => {
    if (!service) return;
    try {
      setIsSharing(true);
      setError(null);
      await service.shareWithUser(entityId, user.uuid, sharePermission);
      setSuccessMessage(`Shared with ${user.name}`);
      setSearchQuery("");
      setSearchResults([]);
      await loadShares();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const detail = (err as Record<string, unknown> | undefined)?.data;
      const detailObj = detail as Record<string, unknown> | undefined;
      setError(
        typeof detailObj?.detail === "string" ? detailObj.detail : `Failed to share ${entityLabel.toLowerCase()}`,
      );
    } finally {
      setIsSharing(false);
    }
  };

  const handleRemoveShare = async (userUuid: string) => {
    if (!service) return;
    try {
      setRemovingId(userUuid);
      setError(null);
      await service.removeShare(entityId, userUuid);
      setShares((prev) => prev.filter((s) => s.sharedWith.uuid !== userUuid));
    } catch (err) {
      console.error("Failed to remove share:", err);
      setError("Failed to remove share");
    } finally {
      setRemovingId(null);
    }
  };

  const handleShareWithTeam = async (team: Team) => {
    try {
      setSharingTeamId(team.uuid);
      setError(null);
      await sharingService.shareWithTeam({
        entityType,
        entityUuid: entityId,
        teamUuid: team.uuid,
        permission: teamSharePermission,
      });
      setSuccessMessage(`Shared with ${team.name}`);
      await loadTeamShares();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const detail = (err as Record<string, unknown> | undefined)?.data;
      const detailObj = detail as Record<string, unknown> | undefined;
      setError(typeof detailObj?.detail === "string" ? detailObj.detail : `Failed to share with team`);
    } finally {
      setSharingTeamId(null);
    }
  };

  const handleRemoveTeamShare = async (shareUuid: string) => {
    try {
      setRemovingTeamShareId(shareUuid);
      setError(null);
      await sharingService.removeTeamShare(shareUuid);
      setTeamShares((prev) => prev.filter((s) => s.uuid !== shareUuid));
    } catch (err) {
      console.error("Failed to remove team share:", err);
      setError("Failed to remove team share");
    } finally {
      setRemovingTeamShareId(null);
    }
  };

  const handleCreateLink = async () => {
    if (!service) return;
    try {
      setIsCreatingLink(true);
      setError(null);
      const response = await service.createShareLink(entityId, linkPermission);
      setLinks((prev) => [...prev, response.data]);
      setSuccessMessage("Share link created");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const detail = (err as Record<string, unknown> | undefined)?.data;
      const detailObj = detail as Record<string, unknown> | undefined;
      setError(typeof detailObj?.detail === "string" ? detailObj.detail : "Failed to create link");
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleCopyLink = async (link: ShareLinkResponse) => {
    if (!shareLinkPath) return;
    try {
      const url = `${globalThis.location.origin}${shareLinkPath}${link.token}`;
      await navigator.clipboard.writeText(url);
      setCopiedLinkId(link.uuid);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleRemoveLink = async (linkUuid: string) => {
    if (!service) return;
    try {
      setRemovingLinkId(linkUuid);
      setError(null);
      await service.removeShareLink(entityId, linkUuid);
      setLinks((prev) => prev.filter((l) => l.uuid !== linkUuid));
    } catch (err) {
      console.error("Failed to deactivate link:", err);
      setError("Failed to deactivate link");
    } finally {
      setRemovingLinkId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <dialog // NOSONAR — native <dialog> requires showModal()/close() lifecycle management; using open attribute with custom overlay instead
      open
      className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4 m-0 max-w-none max-h-none w-full h-full border-none"
      aria-modal="true"
      aria-labelledby="entity-share-dialog-title"
    >
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-primary flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-brand-primary-subtle flex items-center justify-center">
              <Users className="h-5 w-5 text-brand-primary" />
            </div>
            <div>
              <h2 id="entity-share-dialog-title" className="text-lg font-semibold text-content-primary">
                Share {entityLabel}
              </h2>
              <p className="text-xs text-content-quaternary truncate max-w-[280px]">{entityTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close share dialog"
            className="p-2 hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-primary" role="tablist" aria-label="Sharing method">
          {hasFullSharing && (
            <button
              role="tab"
              aria-selected={activeTab === "people"}
              onClick={() => setActiveTab("people")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
                activeTab === "people"
                  ? "text-brand-primary border-b-2 border-brand-primary"
                  : "text-content-tertiary hover:text-content-primary"
              }`}
            >
              <UserPlus className="w-4 h-4" />
              People
            </button>
          )}
          <button
            role="tab"
            aria-selected={activeTab === "team"}
            onClick={() => setActiveTab("team")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
              activeTab === "team"
                ? "text-brand-primary border-b-2 border-brand-primary"
                : "text-content-tertiary hover:text-content-primary"
            }`}
          >
            <Building2 className="w-4 h-4" />
            Team
          </button>
          {hasFullSharing && (
            <button
              role="tab"
              aria-selected={activeTab === "link"}
              onClick={() => setActiveTab("link")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
                activeTab === "link"
                  ? "text-brand-primary border-b-2 border-brand-primary"
                  : "text-content-tertiary hover:text-content-primary"
              }`}
            >
              <Link2 className="w-4 h-4" />
              Link
            </button>
          )}
        </div>

        {/* Error / Success Messages */}
        {error && (
          <div className="mx-6 mt-4 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mx-6 mt-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg text-sm text-green-400">
            {successMessage}
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-4 max-h-[400px] overflow-y-auto">
          {(() => {
            if (isLoadingShares || (activeTab === "team" && isLoadingTeamShares)) {
              return (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                </div>
              );
            }
            if (activeTab === "people") {
              return (
                <PeopleTab
                  sharePermission={sharePermission}
                  onSharePermissionChange={setSharePermission}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  isSearching={isSearching}
                  searchResults={searchResults}
                  isSharing={isSharing}
                  onShareWithUser={handleShareWithUser}
                  shares={shares}
                  removingId={removingId}
                  onRemoveShare={handleRemoveShare}
                />
              );
            }
            if (activeTab === "team") {
              return (
                <TeamTab
                  teams={teams}
                  teamShares={teamShares}
                  teamSharePermission={teamSharePermission}
                  onTeamSharePermissionChange={setTeamSharePermission}
                  sharingTeamId={sharingTeamId}
                  onShareWithTeam={handleShareWithTeam}
                  removingTeamShareId={removingTeamShareId}
                  onRemoveTeamShare={handleRemoveTeamShare}
                  entityLabel={entityLabel}
                />
              );
            }
            return (
              <LinkTab
                links={links}
                linkPermission={linkPermission}
                onLinkPermissionChange={setLinkPermission}
                isCreatingLink={isCreatingLink}
                onCreateLink={handleCreateLink}
                copiedLinkId={copiedLinkId}
                onCopyLink={handleCopyLink}
                removingLinkId={removingLinkId}
                onRemoveLink={handleRemoveLink}
                shareLinkPath={shareLinkPath}
                entityLabel={entityLabel}
              />
            );
          })()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-primary flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Tab sub-components — extracted to reduce EntityShareDialog complexity
// ---------------------------------------------------------------------------

function PeopleTab({
  sharePermission,
  onSharePermissionChange,
  searchQuery,
  onSearchQueryChange,
  isSearching,
  searchResults,
  isSharing,
  onShareWithUser,
  shares,
  removingId,
  onRemoveShare,
}: {
  sharePermission: SharePermission;
  onSharePermissionChange: (v: SharePermission) => void;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  isSearching: boolean;
  searchResults: UserSearchResult[];
  isSharing: boolean;
  onShareWithUser: (user: UserSearchResult) => void;
  shares: ShareResponse[];
  removingId: string | null;
  onRemoveShare: (uuid: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-content-tertiary">Permission:</span>
        <PermissionSelect value={sharePermission} onChange={onSharePermissionChange} />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-9 pr-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary text-sm"
          autoFocus
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary animate-spin" />
        )}
      </div>

      {searchResults.length > 0 && (
        <div className="space-y-1 border border-border-secondary rounded-lg overflow-hidden">
          {searchResults.map((user) => (
            <button
              key={user.uuid}
              onClick={() => onShareWithUser(user)}
              disabled={isSharing}
              className="w-full flex items-center gap-3 p-3 hover:bg-interactive-hover transition text-left disabled:opacity-50"
            >
              <div className="w-8 h-8 bg-linear-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-white">
                  {user.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-content-primary font-medium truncate">{user.name}</div>
                <div className="text-xs text-content-quaternary truncate">{user.email}</div>
              </div>
              <UserPlus className="w-4 h-4 text-content-tertiary shrink-0" />
            </button>
          ))}
        </div>
      )}

      {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
        <p className="text-sm text-content-quaternary text-center py-2">No users found</p>
      )}

      {shares.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-2">
            Shared with ({shares.length})
          </h3>
          <div className="space-y-1">
            {shares.map((share) => (
              <div key={share.uuid} className="flex items-center gap-3 p-3 bg-surface-secondary/50 rounded-lg">
                <div className="w-8 h-8 bg-linear-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-white">
                    {share.sharedWith.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-content-primary font-medium truncate">{share.sharedWith.name}</div>
                  <div className="text-xs text-content-quaternary truncate">{share.sharedWith.email}</div>
                </div>
                <PermissionBadge permission={share.permission} />
                <button
                  onClick={() => onRemoveShare(share.sharedWith.uuid)}
                  disabled={removingId === share.sharedWith.uuid}
                  className="p-1.5 hover:bg-red-500/20 text-content-tertiary hover:text-red-400 rounded-lg transition disabled:opacity-50"
                  title="Remove access"
                >
                  {removingId === share.sharedWith.uuid ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TeamTab({
  teams,
  teamShares,
  teamSharePermission,
  onTeamSharePermissionChange,
  sharingTeamId,
  onShareWithTeam,
  removingTeamShareId,
  onRemoveTeamShare,
  entityLabel,
}: Readonly<{
  teams: Team[];
  teamShares: TeamShareResponse[];
  teamSharePermission: SharePermission;
  onTeamSharePermissionChange: (v: SharePermission) => void;
  sharingTeamId: string | null;
  onShareWithTeam: (team: Team) => void;
  removingTeamShareId: string | null;
  onRemoveTeamShare: (uuid: string) => void;
  entityLabel: string;
}>) {
  const sharedTeamUuids = new Set(teamShares.map((s) => s.teamUuid));
  const unsharedTeams = teams.filter((t) => !sharedTeamUuids.has(t.uuid));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-content-tertiary">Permission:</span>
        <PermissionSelect value={teamSharePermission} onChange={onTeamSharePermissionChange} />
      </div>

      {teams.length > 0 ? (
        <div className="space-y-1">
          {unsharedTeams.length > 0 && (
            <>
              <h3 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-2">Your Teams</h3>
              {unsharedTeams.map((team) => (
                <button
                  key={team.uuid}
                  onClick={() => onShareWithTeam(team)}
                  disabled={sharingTeamId === team.uuid}
                  className="w-full flex items-center gap-3 p-3 hover:bg-interactive-hover rounded-lg transition text-left disabled:opacity-50"
                >
                  <div className="w-8 h-8 bg-linear-to-br from-brand-primary/30 to-brand-secondary/30 rounded-full flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-brand-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-content-primary font-medium truncate">{team.name}</div>
                    {team.description && (
                      <div className="text-xs text-content-quaternary truncate">{team.description}</div>
                    )}
                  </div>
                  {sharingTeamId === team.uuid ? (
                    <Loader2 className="w-4 h-4 text-content-tertiary animate-spin shrink-0" />
                  ) : (
                    <UserPlus className="w-4 h-4 text-content-tertiary shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <Building2 className="w-10 h-10 text-surface-tertiary mx-auto mb-2" />
          <p className="text-sm text-content-tertiary">No teams available</p>
          <p className="text-xs text-content-quaternary mt-1">
            Join or create a team to share this {entityLabel.toLowerCase()} with your teammates
          </p>
        </div>
      )}

      {teamShares.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-2">
            Shared with Teams ({teamShares.length})
          </h3>
          <div className="space-y-1">
            {teamShares.map((share) => (
              <div key={share.uuid} className="flex items-center gap-3 p-3 bg-surface-secondary/50 rounded-lg">
                <div className="w-8 h-8 bg-linear-to-br from-brand-primary/30 to-brand-secondary/30 rounded-full flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-brand-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-content-primary font-medium truncate">{share.teamName}</div>
                </div>
                <PermissionBadge permission={share.permission} />
                <button
                  onClick={() => onRemoveTeamShare(share.uuid)}
                  disabled={removingTeamShareId === share.uuid}
                  className="p-1.5 hover:bg-red-500/20 text-content-tertiary hover:text-red-400 rounded-lg transition disabled:opacity-50"
                  title="Remove team access"
                >
                  {removingTeamShareId === share.uuid ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LinkTab({
  links,
  linkPermission,
  onLinkPermissionChange,
  isCreatingLink,
  onCreateLink,
  copiedLinkId,
  onCopyLink,
  removingLinkId,
  onRemoveLink,
  shareLinkPath,
  entityLabel,
}: Readonly<{
  links: ShareLinkResponse[];
  linkPermission: SharePermission;
  onLinkPermissionChange: (v: SharePermission) => void;
  isCreatingLink: boolean;
  onCreateLink: () => void;
  copiedLinkId: string | null;
  onCopyLink: (link: ShareLinkResponse) => void;
  removingLinkId: string | null;
  onRemoveLink: (uuid: string) => void;
  shareLinkPath?: string;
  entityLabel: string;
}>) {
  const activeLinks = links.filter((l) => l.isActive);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <PermissionSelect value={linkPermission} onChange={onLinkPermissionChange} />
        <button
          onClick={onCreateLink}
          disabled={isCreatingLink}
          className="flex items-center gap-2 px-4 py-2 bg-brand-gradient hover:opacity-90 text-white rounded-lg transition text-sm font-medium disabled:opacity-50"
        >
          {isCreatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Create Link
        </button>
      </div>

      {links.length > 0 ? (
        <div>
          <h3 className="text-xs font-medium text-content-tertiary uppercase tracking-wider mb-2">
            Active Links ({activeLinks.length})
          </h3>
          <div className="space-y-2">
            {activeLinks.map((link) => (
              <div key={link.uuid} className="flex items-center gap-3 p-3 bg-surface-secondary/50 rounded-lg">
                <Link2 className="w-4 h-4 text-content-tertiary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-content-primary font-mono truncate">
                    {`${globalThis.location.origin}${shareLinkPath}${link.token.slice(0, 12)}...`}
                  </div>
                  <div className="text-xs text-content-quaternary">
                    Created {new Date(link.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <PermissionBadge permission={link.permission} />
                <button
                  onClick={() => onCopyLink(link)}
                  className="p-1.5 hover:bg-interactive-active text-content-tertiary hover:text-content-primary rounded-lg transition"
                  title="Copy link"
                >
                  {copiedLinkId === link.uuid ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => onRemoveLink(link.uuid)}
                  disabled={removingLinkId === link.uuid}
                  className="p-1.5 hover:bg-red-500/20 text-content-tertiary hover:text-red-400 rounded-lg transition disabled:opacity-50"
                  title="Deactivate link"
                >
                  {removingLinkId === link.uuid ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <Link2 className="w-10 h-10 text-surface-tertiary mx-auto mb-2" />
          <p className="text-sm text-content-tertiary">No share links yet</p>
          <p className="text-xs text-content-quaternary mt-1">
            Create a link to share this {entityLabel.toLowerCase()} with anyone on the platform
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper sub-components
// ---------------------------------------------------------------------------

function PermissionSelect({
  value,
  onChange,
}: Readonly<{
  value: SharePermission;
  onChange: (v: SharePermission) => void;
}>) {
  return (
    <div className="flex items-center gap-1 bg-surface-secondary rounded-lg border border-border-secondary p-0.5">
      <button
        onClick={() => onChange("read")}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
          value === "read"
            ? "bg-surface-tertiary text-content-primary"
            : "text-content-tertiary hover:text-content-primary"
        }`}
      >
        <Shield className="w-3.5 h-3.5" />
        Read
      </button>
      <button
        onClick={() => onChange("read_write")}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
          value === "read_write"
            ? "bg-surface-tertiary text-content-primary"
            : "text-content-tertiary hover:text-content-primary"
        }`}
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        Read & Write
      </button>
    </div>
  );
}

function PermissionBadge({ permission }: Readonly<{ permission: SharePermission }>) {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
        permission === "read_write"
          ? "bg-brand-secondary-subtle text-brand-secondary border border-brand-secondary/30"
          : "bg-surface-tertiary text-content-tertiary border border-border-hover"
      }`}
    >
      {permission === "read_write" ? "Read & Write" : "Read"}
    </span>
  );
}
